/**
 * Motor de segmentación de puntos HOP.
 *
 * Toda la lógica de filtrado/centrado que antes vivía embebida en el
 * getServerSideProps de la página vive acá para poder testearse en aislamiento.
 * Funciones puras. `fetchHopPuntos` (I/O) está en `hopPuntosServer.js`.
 */

const MAX_TERM_LENGTH = 100;
const MAX_LIMIT = 300;

/**
 * Determina si un punto tiene capacidad de venta de envíos (reseller).
 * Soporta `is_reseller` (0|1 entero del backend GPDE HOP2-5276), `reseller`
 * (mapeado a boolean desde is_reseller), y el legacy `seller` (string).
 * @param {object} p
 * @returns {boolean}
 */
const isResellerPoint = (p) => {
  if (!p) return false;
  const r = p.is_reseller ?? p.reseller ?? p.seller;
  if (r == null) return false;
  if (typeof r === 'boolean') return r;
  if (typeof r === 'number') return r === 1;
  return String(r).trim() !== '' && String(r) !== '0';
};

// Campos de segmentación soportados. Los de match exacto vs. parcial se
// distinguen en `matchesSegment`. `observation` y `observations` son alias:
// el campo real del punto es plural.
const SEGMENT_FIELDS = {
  agency_code: (p) => normalize(p.agency_code),
  zip_code: (p) => normalize(p.zip_code),
  observation: (p) => normalize(p.observations),
  observations: (p) => normalize(p.observations),
  seller: (p) => normalize(p.is_reseller ?? p.reseller ?? p.seller),
  reseller: (p) => normalize(p.is_reseller ?? p.reseller ?? p.seller),
  name: (p) => normalize(p.name)
};
const EXACT_SEGMENT_FIELDS = new Set(['agency_code', 'zip_code']);

// Params que activan el motor de segmentación (todo salvo `focus`, que sólo
// elige el botón inicial de la UI y no filtra datos ni marca noindex).
const ENGINE_KEYS = [
  'mode',
  'segmentBy',
  'segmentValue',
  'q',
  'searchBy',
  'set',
  'limit',
  'center',
  'centerLat',
  'centerLng',
  'zoom'
];

/**
 * Normaliza un string para comparar: sin acentos, minúsculas, sin bordes.
 * @param {*} value
 * @returns {string}
 */
const normalize = (value) =>
  (value == null ? '' : value)
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Convierte la whitelist CSV (`set`) en una lista de valores normalizados.
 * @param {string|string[]} value
 * @returns {string[]}
 */
const parseSet = (value) => {
  if (!value) return [];
  const source = Array.isArray(value) ? value.join(',') : value;
  return source
    .split(',')
    .map((item) => normalize(item))
    .filter(Boolean);
};

/**
 * @param {object} point
 * @param {string[]} setValues valores ya normalizados
 */
const matchesSet = (point, setValues) => {
  if (!setValues || setValues.length === 0) return true;
  const agencyCode = normalize(point.agency_code);
  const pointId = normalize(point.id);
  return setValues.includes(agencyCode) || setValues.includes(pointId);
};

/**
 * @param {object} point
 * @param {string} segmentBy campo a segmentar
 * @param {string} target valor de segmento YA normalizado
 */
const matchesSegment = (point, segmentBy, target) => {
  if (!segmentBy || !target) return true;
  const getter = SEGMENT_FIELDS[segmentBy];
  // Un segmentBy no soportado NO puede desactivar el filtro en silencio:
  // si se pidió segmentar por algo que no existe, no matchea nada.
  if (!getter) return false;
  const value = getter(point);
  return EXACT_SEGMENT_FIELDS.has(segmentBy) ? value === target : value.includes(target);
};

/**
 * @param {object} point
 * @param {string} term término de búsqueda YA normalizado
 */
const matchesSearch = (point, term, searchBy) => {
  if (!term) return true;
  if (searchBy === 'point') {
    return normalize(point.name).includes(term) || normalize(point.agency_code).includes(term);
  }
  return (
    normalize(point.name).includes(term) ||
    normalize(point.full_address).includes(term) ||
    normalize(point.agency_code).includes(term) ||
    normalize(point.zip_code).includes(term)
  );
};

/**
 * Resuelve el límite por categoría.
 * Devuelve `null` (sin recorte) cuando no vino un límite explícito y válido,
 * para que la página comercial no se trunque nunca por defecto.
 * @param {*} limit
 * @returns {number|null}
 */
const resolveLimit = (limit) => {
  if (limit === undefined || limit === null || String(limit).trim() === '') return null;
  const parsed = typeof limit === 'number' ? limit : parseInt(limit, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.min(parsed, MAX_LIMIT);
};

/**
 * Filtra los puntos por modo + segmento + búsqueda + whitelist.
 * @param {{ pickup: object[], dropOff: object[] }} points
 * @returns {{ pickup: object[], dropOff: object[] }}
 */
const filterPoints = ({ points, mode, segmentBy, segmentValue, q, searchBy, limit, setValues }) => {
  const onlyPickup = mode === 'pickup';
  const onlyDropoff = mode === 'dropoff';
  const onlySeller = mode === 'seller';
  const sellerDataAvailable = [...(points?.pickup || []), ...(points?.dropOff || [])].some(isResellerPoint);
  const safeLimit = resolveLimit(limit);

  // Precomputado una sola vez, no por punto.
  const term = normalize(q).slice(0, MAX_TERM_LENGTH);
  const segTarget = normalize(segmentValue);
  const segField = normalize(segmentBy);
  const matchesSeller = (point) => {
    if (!onlySeller) return true;
    if (sellerDataAvailable) return isResellerPoint(point);
    return Number(point.allow_drop_off) === 1;
  };

  const apply = (list) => {
    const filtered = (list || []).filter(
      (point) =>
        matchesSeller(point) &&
        matchesSegment(point, segField, segTarget) &&
        matchesSearch(point, term, normalize(searchBy)) &&
        matchesSet(point, setValues)
    );
    return safeLimit ? filtered.slice(0, safeLimit) : filtered;
  };

  return {
    pickup: onlyDropoff ? [] : apply(points?.pickup),
    dropOff: onlyPickup ? [] : apply(points?.dropOff)
  };
};

/**
 * Centro geográfico (bounding-box) de los puntos filtrados.
 * @returns {{ lat: number, lng: number } | null}
 */
const computeCenterFromPoints = (points) => {
  const coords = [...(points?.pickup || []), ...(points?.dropOff || [])]
    .map((point) => ({ lat: Number(point.lat), lng: Number(point.lng) }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

  if (coords.length === 0) return null;

  const lats = coords.map((c) => c.lat);
  const lngs = coords.map((c) => c.lng);
  return {
    lat: (Math.min(...lats) + Math.max(...lats)) / 2,
    lng: (Math.min(...lngs) + Math.max(...lngs)) / 2
  };
};

/**
 * Devuelve una coordenada finita o `null`.
 * Clave: `Number('')` es 0 y pasaría un `isFinite`, centrando el mapa en (0,0);
 * por eso rechazamos vacíos y no-strings antes de convertir.
 */
const toCoord = (value) => {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toPositiveInt = (value) => {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Aplica el centro/zoom del mapa según los params, sin mutar el dict original.
 * @returns {object} copia del dict con `map` ajustado
 */
const applyMapCenter = (dict, points, params) => {
  const next = { ...dict, map: { ...dict.map } };

  if (params.center === 'auto') {
    const auto = computeCenterFromPoints(points);
    if (auto) {
      next.map.lat = auto.lat;
      next.map.lng = auto.lng;
    }
  }

  if (params.centerLat !== null && params.centerLng !== null) {
    next.map.lat = params.centerLat;
    next.map.lng = params.centerLng;
  }

  if (params.zoom !== null) {
    next.map.zoom = params.zoom;
  }

  return next;
};

/**
 * Parsea la query en un objeto de params estable.
 *
 * `focus` sólo elige el botón inicial (no filtra datos del server). El resto de
 * los params activan el motor: `active` indica si hay que correr el motor
 * (filtrar + recentrar) y marcar la URL como noindex; `focus` solo no lo activa.
 *
 * @param {object} query
 */
const parseHopQuery = (query = {}) => {
  const focus = normalize(query.focus);
  const focusedMode =
    focus === 'pickup' || focus === 'dropoff'
      ? focus
      : focus === 'buy' || focus === 'sell'
        ? 'buy'
      : focus === 'seller' || focus === 'reseller'
        ? 'seller'
        : '';

  const has = (key) =>
    query[key] !== undefined && query[key] !== null && String(query[key]).trim() !== '';
  const engineRequested = ENGINE_KEYS.some(has);

  const rawMode = engineRequested ? normalize(query.mode) : '';
  const validMode = ['all', 'pickup', 'dropoff', 'seller', 'reseller'].includes(rawMode)
    ? rawMode === 'reseller'
      ? 'seller'
      : rawMode
    : 'all';
  // focus tiene prioridad sobre mode para definir el modo efectivo.
  const effectiveMode = focusedMode || validMode;
  // searchMode (UI) deriva del modo efectivo, no sólo de focus: así `?mode=pickup`
  // no deja botones cuyo dataset el server ya vació.
  const searchMode =
    effectiveMode === 'pickup' || effectiveMode === 'dropoff' || effectiveMode === 'buy'
      ? effectiveMode
      : 'all';

  return {
    active: engineRequested,
    searchMode,
    mode: effectiveMode,
    segmentBy: engineRequested ? normalize(query.segmentBy) : '',
    segmentValue: engineRequested ? query.segmentValue || '' : '',
    q: engineRequested ? query.q || '' : '',
    searchBy: engineRequested ? normalize(query.searchBy) : '',
    limit: engineRequested ? query.limit ?? null : null,
    setValues: engineRequested ? parseSet(query.set) : [],
    center: engineRequested ? normalize(query.center) : 'none',
    centerLat: engineRequested ? toCoord(query.centerLat) : null,
    centerLng: engineRequested ? toCoord(query.centerLng) : null,
    zoom: engineRequested ? toPositiveInt(query.zoom) : null
  };
};

/**
 * Trae los puntos desde el endpoint interno.
 * @see utils/hopPuntosServer.js (extraído para no arrastrar logger al cliente)
 */

/**
 * Devuelve solo los puntos habilitados para gestión de envíos por revendedores.
 * Usa `isResellerPoint` (soporta `is_reseller` 0|1, `reseller` boolean, `seller` string).
 * Como fallback (mientras el backend no devuelva `is_reseller`), incluye los puntos
 * con `allow_drop_off === 1`, para que la sección no quede vacía.
 * @param {{ pickup: object[], dropOff: object[] }} points
 * @returns {{ pickup: object[], dropOff: object[] }}
 */
const hasReseller = (p) => {
  if (isResellerPoint(p)) return true;
  // fallback: si no hay seller, incluir puntos con allow_drop_off habilitado
  if (p.allow_drop_off != null && Number(p.allow_drop_off) === 1) return true;
  return false;
};

const filterReseller = (points) => ({
  pickup: (points?.pickup || []).filter(hasReseller),
  dropOff: (points?.dropOff || []).filter(hasReseller),
});

module.exports = {
  normalize,
  parseSet,
  matchesSet,
  matchesSegment,
  matchesSearch,
  resolveLimit,
  isResellerPoint,
  filterPoints,
  filterReseller,
  computeCenterFromPoints,
  applyMapCenter,
  parseHopQuery,
};
